import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import Hero from "./Hero";
import SellImage from "../media/Landing_humanm/Springfarm(1).jpeg";
import BuyImage from "../media/Landing_humanm/Emuplain(1).jpg";
import RentImage from "../media/Landing_humanm/Springfarm2(1).jpeg";
import ForSellImage from "../media/Landing_humanm/wilton(2).jpeg";
import ImageA from "../media/Landing_humanm/Emu plain2(1).jpeg";
import ImageB from "../media/Landing_humanm/Everyday(2).jpg";
import ImageC from "../media/Landing_humanm/Lakemba(1).jpeg";
import PropertyCard from "./PropertyCard";
import PropertyModal from "./PropertyModal";
import RecentlySold from "./RecentlySold";
import "./HomePage.css";
import { FaHome, FaMapMarkerAlt } from "react-icons/fa";
import PropertyCarousel from "./PropertyCarousel";
import MakeOffer from "./Makeoffer";
import BuySellRent from "./components/BuySellRent";
import RequestAppraisalOverlay from "./RequestAppraisalOverlay";

// Disable noisy console logs in production (keep warnings/errors)
if (
  typeof process !== "undefined" &&
  process.env &&
  process.env.NODE_ENV === "production"
) {
  try {
    // eslint-disable-next-line no-console
    console.log = () => { };
    // eslint-disable-next-line no-console
    console.debug = () => { };
  } catch (_) { } // eslint-disable-line no-empty
}

const LISTINGS_API_URL =
  "https://868qsxaw23.execute-api.us-east-2.amazonaws.com/Prod/listings";
const MEDIA_API_URL =
  "https://868qsxaw23.execute-api.us-east-2.amazonaws.com/Prod/media";
const CLOUDFRONT_BASE_URL = "https://dx9e0rbpjsaqb.cloudfront.net/";

const HomePage = () => {
  const navigate = useNavigate();
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [isAppraisalOverlayOpen, setAppraisalOverlayOpen] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const openAppraisalOverlay = () => setAppraisalOverlayOpen(true);
  const closeAppraisalOverlay = () => setAppraisalOverlayOpen(false);

  const reviews = [
    {
      rating: 5,
      label: "Verified review",
      title: "Seller of house in Blair Athol, NSW",
      time: "1 year ago",
      text: "I recently sold my home with Abhi and was very pleased. His professionalism and skills were outstanding. Highly recommended.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Buyer of house in Austral, NSW",
      time: "1 year 1 month ago",
      text: "The buying process was smooth. Abhi was very helpful and kept us informed the whole time. He explained everything clearly and guided us.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Seller of house in Austral, NSW",
      time: "1 year 1 month ago",
      text: "Sold my property through Abhi and had an excellent experience. He was professional, communicative, and secured a great price. Highly satisfied with the outcome.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Buyer of house in Blair Athol, NSW",
      time: "1 year 1 month ago",
      text: "I recently bought a home through Abhi and had a great experience. He was patient, knowledgeable, and provided clear advice, making everything easy. I'm very happy with the home he found for me.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Seller of house in Austral, NSW",
      time: "1 year 1 month ago",
      text: "It was a great experience working with Abhi. He was professional, knowledgeable, and always available to help. He made the process smooth and easy. Highly recommend!",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Buyer of house in Austral, NSW",
      time: "1 year 3 months ago",
      text: "Abhi was very helpful to us in our purchase. We really appreciated how much he helped us negotiate every step along the way. We found him to be friendly, honest and professional.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Buyer of residential land in Leppington, NSW",
      time: "1 year 4 months ago",
      text: "Abhi is really amazing. He helped us a lot to seal the deal. He was always quick, patient, and understood our situation well. We liked how he was always honest and clear with us. He made everything seem easy. Thanks a lot, Abhi! We hope to work with you again in the future.",
    },
    {
      rating: 5,
      label: "Verified review",
      title: "Buyer of residential land in Leppington, NSW",
      time: "1 year 4 months ago",
      text: "Abhi was exceptional in helping me purchase land in Leppington, demonstrating deep market knowledge and outstanding professionalism. He provided invaluable insights, was always available for questions, and secured a great deal. Highly recommend Abhi for his dedication and expertise.",
    },
  ];
  const images = [
    { src: BuyImage, alt: "Happy couple buying their home" },
    { src: ForSellImage, alt: "Family selling their home" },
    { src: RentImage, alt: "House Rented Successfully" },
    { src: SellImage, alt: "Family selling their home" },
    { src: ImageA, alt: "A happy family" },
    { src: ImageB, alt: "A happy couple" },
    { src: ImageC, alt: "A beautiful house" },
  ];
  // Page load animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const sectionRefs = useRef({});
  const observerRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => setPageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const setSectionRef = (id) => (el) => {
    if (el && observerRef.current) {
      sectionRefs.current[id] = el;
      observerRef.current.observe(el);
    }
  };

  const handlePropertyClick = async (property) => {
    setIsModalOpen(true);

    try {
      const imageUrls = await getAllImagesFromMedia(property.media);
      setSelectedProperty({ ...property, images: imageUrls });
    } catch (error) {
      console.error("Error loading images for modal:", error);
      setSelectedProperty(property);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProperty(null);
  };

  const handleOpenOfferModal = () => {
    setIsModalOpen(false); // Close property modal, but keep selectedProperty
    setIsOfferModalOpen(true); // Open offer modal
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const refs = sectionRefs.current;
    return () => {
      Object.values(refs).forEach((ref) => {
        if (ref && observerRef.current) {
          observerRef.current.unobserve(ref);
        }
      });
    };
  }, []);

  const parseCsv = (csv) => {
    const rows = [];
    if (!csv) return rows;

    const lines = [];
    let currentLine = "";
    let inQuotes = false;
    let i = 0;

    while (i < csv.length) {
      const char = csv[i];
      const nextChar = csv[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentLine += '"';
          i += 2;
        } else {
          inQuotes = !inQuotes;
          currentLine += char;
          i++;
        }
      } else if (char === "\n" && !inQuotes) {
        lines.push(currentLine);
        currentLine = "";
        i++;
      } else if (char === "\r" && nextChar === "\n" && !inQuotes) {
        lines.push(currentLine);
        currentLine = "";
        i += 2;
      } else {
        currentLine += char;
        i++;
      }
    }
    if (currentLine.trim()) {
      lines.push(currentLine);
    }

    if (lines.length < 2) return rows;

    const parseCsvLine = (line) => {
      const values = [];
      let current = "";
      let inQuotes = false;
      let i = 0;
      while (i < line.length) {
        const char = line[i];
        const nextChar = line[i + 1];
        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            current += '"';
            i += 2;
          } else {
            inQuotes = !inQuotes;
            i++;
          }
        } else if (char === "," && !inQuotes) {
          values.push(current);
          current = "";
          i++;
        } else {
          current += char;
          i++;
        }
      }
      values.push(current);
      return values.map((v) => v.replace(/""/g, '"'));
    };

    const headers = parseCsvLine(lines[0]);

    for (let i = 1; i < lines.length; i++) {
      const values = parseCsvLine(lines[i]);
      if (values.length === headers.length) {
        const obj = {};
        headers.forEach((header, index) => {
          let value = values[index] || "";
          if (value.toLowerCase() === "home and land packages") {
            value = "Home & Land";
          }
          obj[header] = value;
        });
        rows.push(obj);
      }
    }
    return rows;
  };

  const toNumber = (val) => {
    const n = Number(val);
    return Number.isFinite(n) ? n : undefined;
  };

  // Build CloudFront URL for media key
  const buildCloudFrontUrl = (mediaKey) => {
    const key = mediaKey.startsWith("/") ? mediaKey.slice(1) : mediaKey;
    return `${CLOUDFRONT_BASE_URL}${key}`;
  };

  // Extract all images from media array with CSV formatting fixes
  const getAllImagesFromMedia = useCallback(async (mediaString) => {
    if (!mediaString) return [];
    try {
      let mediaKeys = [];
      let cleanedString = mediaString.toString();
      if (cleanedString.startsWith('"') && cleanedString.endsWith('"')) {
        cleanedString = cleanedString.slice(1, -1);
      }
      cleanedString = cleanedString.replace(/""/g, '"');

      if (cleanedString.startsWith("[") && cleanedString.endsWith("]")) {
        const content = cleanedString.slice(1, -1);
        if (content.trim()) {
          mediaKeys = content.split(",").map((key) => key.trim());
        }
      } else {
        mediaKeys = JSON.parse(cleanedString);
      }

      if (!Array.isArray(mediaKeys) || mediaKeys.length === 0) return [];

      const filteredKeys = mediaKeys.filter((key) => {
        if (typeof key !== "string") return false;
        const extension = key.toLowerCase().split(".").pop();
        const allowedFormats = [
          "jpg",
          "jpeg",
          "png",
          "webp",
          "gif",
          "bmp",
          "svg",
          "pdf",
        ];
        return allowedFormats.includes(extension);
      });

      if (filteredKeys.length === 0) return [];

      // Sort: Images first, PDFs last
      const sortedKeys = [...filteredKeys].sort((a, b) => {
        const isPdfA = a.toLowerCase().split("?")[0].endsWith(".pdf");
        const isPdfB = b.toLowerCase().split("?")[0].endsWith(".pdf");
        if (isPdfA && !isPdfB) return 1;
        if (!isPdfA && isPdfB) return -1;
        return 0;
      });

      return sortedKeys.map((imageKey) => buildCloudFrontUrl(imageKey));
    } catch (error) {
      // Fallback parsing for malformed media strings
      try {
        if (
          typeof mediaString === "string" &&
          mediaString.includes("media/") &&
          !mediaString.includes("[")
        ) {
          const cleanPath = mediaString.replace(/['"]/g, "").trim();
          const extension = cleanPath.toLowerCase().split(".").pop();
          if (
            ["jpg", "jpeg", "png", "webp", "gif", "bmp", "svg", "pdf"].includes(
              extension
            )
          ) {
            return [buildCloudFrontUrl(cleanPath)];
          }
        }
        const mediaPathRegex =
          /media\/.*?\.(jpg|jpeg|png|gif|webp|bmp|svg|pdf)(?=["\'\s\]}\,]|$)/gi;
        const matches = mediaString.match(mediaPathRegex);
        if (matches && matches.length > 0) {
          const processedMatches = matches.map((imagePath) =>
            imagePath.trim()
          );
          
          // Sort fallback matches: Images first, PDFs last
          const sortedMatches = processedMatches.sort((a, b) => {
            const isPdfA = a.toLowerCase().split("?")[0].endsWith(".pdf");
            const isPdfB = b.toLowerCase().split("?")[0].endsWith(".pdf");
            if (isPdfA && !isPdfB) return 1;
            if (!isPdfA && isPdfB) return -1;
            return 0;
          });

          return sortedMatches.map((path) => buildCloudFrontUrl(path));
        }
      } catch (altError) {
        console.error("❌ Alternative parsing also failed:", altError);
      }
      return [];
    }
  }, []);

  // Sample property data (keeping as fallback)
  const fallbackProperties = useMemo(
    () => [
      // ... (existing fallback properties are kept)
    ],
    []
  );

  // Load real properties from API
  useEffect(() => {
    const loadFeaturedProperties = async () => {
      setIsLoading(true);
      try {
        const res = await fetch(LISTINGS_API_URL);
        if (!res.ok)
          throw new Error(`Failed to load listings: HTTP ${res.status}`);
        const data = await res.json();
        const csv = typeof data === "string" ? data : data.csv || "";
        const rows = parseCsv(csv);

        const mapped = rows
          .map((r, idx) => ({
            id: r.id || `${r.address || "property"}-${idx}`,
            address: r.address || "",
            suburb: r.suburb || "",
            propertyType: r.propertyType || r.property_type || "",
            lot: r.lot || "",
            availability: r.availability || "",
            status: r.status || "For Sale",
            registrationConstructionStatus:
              r.registrationConstructionStatus ||
              r.registration ||
              r.constructionStatus ||
              "",
            price: r.price || r.price_guide || "Price on request",
            frontage: toNumber(r.frontage),
            landSize: toNumber(r.landSize) || toNumber(r.land_area_sqm),
            bedrooms: toNumber(r.bed),
            bathrooms: toNumber(r.bath),
            parking: toNumber(r.garage),
            media: r.media || r.media_url || "",
            remark: r.remark || r.Remark || r.REMARK || "",
            description: r.description || r.Description || r.DESCRIPTION || "",
            image: "", // Start with a placeholder or empty
            images: [], // For modal
            priceNumber: (() => {
              const raw = (r.price || r.price_guide || "").toString();
              const n = Number(raw.replace(/[^0-9]/g, ""));
              return Number.isFinite(n) ? n : undefined;
            })(),
            propertyCustomerVisibility: r.propertyCustomerVisibility || "1",
            priceCustomerVisibility: r.priceCustomerVisibility || "0",
          }))
          .filter(
            (p) =>
              (p.address || p.suburb) && p.propertyCustomerVisibility === "1"
          );

        const available = mapped.filter(
          (p) => p.availability?.toLowerCase().trim() === "available"
        );

        if (available.length === 0) {
          setFeaturedProperties(fallbackProperties);
          setIsLoading(false);
          return;
        }

        const propertiesWithImageData = await Promise.all(
          available.map(async (property) => {
            const mediaUrls = await getAllImagesFromMedia(property.media);
            // Find the first actual image (not PDF)
            const firstImage = mediaUrls.find(url => !url.toLowerCase().split('?')[0].endsWith('.pdf'));

            return {
              ...property,
              image: firstImage || null,
              images: mediaUrls || [],
            };
          })
        );

        const validProperties = propertiesWithImageData.filter((p) => p.image);

        if (validProperties.length > 0) {
          const shuffleArray = (arr) =>
            arr
              .map((value) => ({ value, sort: Math.random() }))
              .sort((a, b) => a.sort - b.sort)
              .map(({ value }) => value);
          const selected = shuffleArray(validProperties).slice(0, 6);
          setFeaturedProperties(selected);
        } else {
          setFeaturedProperties(fallbackProperties);
        }
      } catch (e) {
        console.error("Error loading featured properties:", e);
        setFeaturedProperties(fallbackProperties);
      } finally {
        setIsLoading(false);
      }
    };
    loadFeaturedProperties();
  }, [fallbackProperties, getAllImagesFromMedia]);

  // Debug logging for render
  console.log(
    "🏠 HomePage render - isLoading:",
    isLoading,
    "featuredProperties.length:",
    featuredProperties.length
  );
  console.log("🏠 Featured properties data:", featuredProperties);

  return (
    <div className="home-page">
      <Hero onOpenAppraisal={openAppraisalOverlay} />

      {/* Featured Properties Section */}
      <section
        id="featured-properties"
        ref={setSectionRef("featured-properties")}
        className={`section featured-properties ${pageLoaded ? "animate-in" : ""
          }`}
      >
        <div className="container">
          <div className="section-header">
            <h2 className="section-title handwriting-font">
              Start Your Smart Property Search
            </h2>
            <p className="section-subtitle">
              Your Gateway to South West Sydney Living.
            </p>
          </div>

          {isLoading ? (
            <div>Loading...</div>
          ) : featuredProperties.length > 0 ? (
            <PropertyCarousel>
              {featuredProperties.map((property) => (
                <PropertyCard
                  property={property}
                  onViewDetails={() => handlePropertyClick(property)}
                />
              ))}
            </PropertyCarousel>
          ) : (
            <div>No properties available.</div>
          )}

          <div
            className="view-all-container"
            style={{ display: "flex", gap: 12, justifyContent: "center" }}
          >
            <button
              className="btn btn-secondary btn-large"
              onClick={() => navigate("/buy")}
            >
              View All Properties
            </button>
          </div>
        </div>
      </section>
      <RecentlySold setSectionRef={setSectionRef} />

      {/* Sell & Buy with AuzLandRE Section */}
      <section
        id="sell-buy-section"
        ref={setSectionRef("sell-buy-section")}
        className={`section sell-buy-section ${pageLoaded ? "animate-in" : ""}`}
      >
        <div className="container">
          <h2
            className={`section-title handwriting-font ${pageLoaded ? "animate-title" : ""
              }`}
          >
            Our Happy Clients
          </h2>
          <div className="reviews-marquee">
            <div className="marquee">
              <div className="marquee-track">
                {[...images, ...images].map((image, i) => (
                  <div className="marquee-card" key={`top-${i}`}>
                    <img src={image.src} alt={image.alt} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Client Satisfaction Section */}
      <section
        id="client-satisfaction"
        ref={setSectionRef("client-satisfaction")}
        className={`section client-satisfaction ${pageLoaded ? "animate-in" : ""
          }`}
      >
        <div className="container">
          <h2
            className={`section-title handwriting-font ${pageLoaded ? "animate-title" : ""
              }`}
          >
            What Our Clients Say
          </h2>
          <p
            className={`section-subtitle ${pageLoaded ? "animate-subtitle" : ""
              }`}
          >
            Real stories from families who found their dream homes with
            AuzLandRE
          </p>

          {/* Removed static cards - using continuous two-lane marquee below */}

          {/* Continuous Marquee Reviews */}
          <div className="reviews-marquee">
            <div className="marquee lane-top">
              <div
                className="marquee-track"
                style={{ animationDirection: "reverse" }}
              >
                {[...reviews, ...reviews].map((r, i) => (
                  <div className="marquee-card" key={`top-${i}`}>
                    <div className="marquee-header">
                      <span className="stars">★★★★★</span>
                      <span className="verify">{r.label}</span>
                    </div>
                    <div className="marquee-title">{r.title}</div>
                    <p className="marquee-text">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="marquee lane-bottom">
              <div className="marquee-track">
                {[...reviews.slice().reverse(), ...reviews].map((r, i) => (
                  <div className="marquee-card" key={`bottom-${i}`}>
                    <div className="marquee-header">
                      <span className="stars">★★★★★</span>
                      <span className="verify">{r.label}</span>
                    </div>
                    <div className="marquee-title">{r.title}</div>
                    <p className="marquee-text">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <PropertyModal
        key={
          selectedProperty
            ? `${selectedProperty.id}-${selectedProperty.images?.length}`
            : "modal"
        }
        property={selectedProperty}
        isOpen={isModalOpen}
        onClose={closeModal}
        onMakeOffer={handleOpenOfferModal}
      />
      <MakeOffer
        property={selectedProperty}
        isOpen={isOfferModalOpen}
        onClose={() => setIsOfferModalOpen(false)}
      />
      <RequestAppraisalOverlay
        isOpen={isAppraisalOverlayOpen}
        onClose={closeAppraisalOverlay}
      />
    </div>
  );
};

export default HomePage;
